import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useImageLibrary } from '@/hooks/runware/image-library';
import { toast } from '@/hooks/errors/use-toast';
import PersistentFavoriteButton from '@/components/ui/PersistentFavoriteButton';


interface ImageDisplayProps {
  imageUrl: string;
  description?: string;
  imageId: string;
  initialFavoriteCount: number;
  initialIsFavorited?: boolean; // NEW: Initial favorite status from pagination data
  hasPrevious?: boolean;
  hasNext?: boolean;
  onPrevious?: (e?: React.MouseEvent) => void;
  onNext?: (e?: React.MouseEvent) => void;
  onWheel?: (e: React.WheelEvent) => void;
  showFavoriteButton?: boolean;
  showDescription?: boolean;
  dropdownLabel?: string;
}

export const ImageDisplay: React.FC<ImageDisplayProps> = ({
  imageUrl,
  description,
  imageId,
  initialFavoriteCount,
  initialIsFavorited,
  hasPrevious = false,
  hasNext = false,
  onPrevious,
  onNext,
  onWheel,
  showFavoriteButton = true,
  showDescription = true,
  dropdownLabel
}) => {
  // Favorite state for PersistentFavoriteButton
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteCount, setFavoriteCount] = useState(initialFavoriteCount);
  
  const { toggleFavorite, checkFavoriteStatus } = useImageLibrary();

  // Reset favorite count when image changes
  useEffect(() => {
    setFavoriteCount(initialFavoriteCount);
  }, [imageId, initialFavoriteCount]);

  // Check favorite status with error handling - only when favorites are enabled
  useEffect(() => {
    // ✅ FIX: Use the favorite status from pagination data instead of making individual API calls
    // This prevents 406 errors when opening images in lightbox during pagination
    if (!imageId || !showFavoriteButton) {
      setIsFavorited(false);
      return;
    }
    
    // Use the provided favorite status from pagination data if available
    setIsFavorited(initialIsFavorited || false);
    
    // ❌ REMOVED: Individual checkFavoriteStatus calls that cause 406 errors during pagination
    // const checkFavoriteStatusAsync = async () => {
    //   try {
    //     const result = await checkFavoriteStatus(imageId);
    //     setIsFavorited(result);
    //   } catch (error) {
    //     console.error('Error checking favorite status:', error);
    //     setIsFavorited(false);
    //   }
    // };
    // checkFavoriteStatusAsync();
  }, [imageId, showFavoriteButton, initialIsFavorited]);

  // Handle favorite toggle for PersistentFavoriteButton
  const handleFavoriteToggle = useCallback(async (itemId: string): Promise<void> => {
    try {
      await toggleFavorite.mutateAsync(itemId);
      
      // Update local state for immediate feedback
      const newFavoriteState = !isFavorited;
      const newCount = newFavoriteState ? favoriteCount + 1 : Math.max(0, favoriteCount - 1);
      
      setIsFavorited(newFavoriteState);
      setFavoriteCount(newCount);
      
      toast({
        title: newFavoriteState ? "Added to favorites" : "Removed from favorites",
        description: `Image ${newFavoriteState ? 'saved to' : 'removed from'} your favorites.`,
        variant: "success"
      });
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        title: "Favorite action failed",
        description: error instanceof Error ? error.message : "Unable to update favorites. Please try again.",
        variant: "error"
      });
    }
  }, [toggleFavorite, isFavorited, favoriteCount]);

  // Navigation with proper event handling
  const handleNavigation = (direction: 'prev' | 'next', e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
    }

    try {
      if (direction === 'prev' && onPrevious) {
        onPrevious(e);
      } else if (direction === 'next' && onNext) {
        onNext(e);
      }
    } catch (error) {
      console.error('Navigation error:', error);
      toast({
        title: "Navigation failed",
        description: "Unable to navigate to the next image. Please try again.",
        variant: "error"
      });
    }
  };

  return (
    <div 
      className="relative flex-1 flex flex-col h-full bg-gradient-to-br from-slate-50/30 to-slate-100/50 dark:from-slate-900/50 dark:to-slate-800/30"
      onWheel={onWheel}
    >
      {/* Main image container */}
      <div className="relative flex-1 flex items-center justify-center p-4 md:p-0 min-h-0">
        {/* Top-left actions container */}
        <AnimatePresence mode="wait">
          {imageId && (
            <motion.div
              key={`actions-${imageId}`}
              className="absolute top-4 left-4 z-40 flex items-center gap-2"
              initial={{ opacity: 0, scale: 0.8, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -20 }}
              transition={{ 
                type: "spring", 
                stiffness: 400, 
                damping: 25,
                delay: 0.3
              }}
            >
              {showFavoriteButton && (
                <PersistentFavoriteButton
                  itemId={imageId}
                  onToggleFavorite={handleFavoriteToggle}
                  isFavorite_prop={isFavorited}
                  favoriteCount_prop={favoriteCount}
                  forceWhiteText={true}
                  data-test-id="favorite-button"
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hover-based navigation zones - like ProfileAvatar lightbox */}
        <AnimatePresence mode="wait">
          {hasPrevious && onPrevious && (
            <motion.div 
              key="nav-previous"
              className="absolute left-0 top-0 w-[15%] h-full cursor-pointer z-30 group select-none" 
              onClick={(e) => handleNavigation('prev', e)}
              onMouseDown={(e) => e.preventDefault()}
              data-test-id="left-nav-zone"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div 
                className="absolute left-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-90 transition-all duration-200 scale-90 group-hover:scale-100"
              >
                <motion.div 
                  className="rounded-full h-12 w-12 flex items-center justify-center bg-black/30 backdrop-filter backdrop-blur-2xl shadow-lg group-hover:bg-black/40"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m15 18-6-6 6-6"/>
                  </svg>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          {hasNext && onNext && (
            <motion.div 
              key="nav-next"
              className="absolute right-0 top-0 w-[15%] h-full cursor-pointer z-30 group select-none" 
              onClick={(e) => handleNavigation('next', e)}
              onMouseDown={(e) => e.preventDefault()}
              data-test-id="right-nav-zone"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div 
                className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-90 transition-all duration-200 scale-90 group-hover:scale-100"
              >
                <motion.div 
                  className="rounded-full h-12 w-12 flex items-center justify-center bg-black/30 backdrop-filter backdrop-blur-2xl shadow-lg group-hover:bg-black/40"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 18 6-6-6-6"/>
                  </svg>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Image - mobile: current size, desktop: large like ProfileAvatar */}
        <motion.img
          key={`image-${imageId}`}
          src={imageUrl}
          alt={description || 'Generated image'}
          className={cn(
            "object-contain rounded-lg shadow-lg",
            // Mobile: current perfect sizing
            "max-w-full max-h-full",
            // Desktop: large sizing like ProfileAvatar with minimum sizes
            "md:max-h-[95vh] md:max-w-[calc(95vw-250px)] md:w-auto md:h-auto md:rounded-none md:shadow-none md:min-w-[500px] md:min-h-[500px]"
          )}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          draggable={false}
        />
      </div>

      {/* Bottom bar - mobile: always visible, desktop: overlay style */}
      {showDescription && description && (
        <div className={cn(
          "flex items-center justify-between p-4 border-t border-border/50",
          // Mobile: current perfect styling
          "bg-white/40 dark:bg-slate-900/40",
          // Desktop: overlay style like ProfileAvatar  
          "md:absolute md:bottom-0 md:left-0 md:right-0 md:bg-black/20 md:backdrop-blur-sm md:border-t-0"
        )}>
          {/* Image description */}
          <div className="flex-1 mx-4">
            <p className={cn(
              "text-sm text-center line-clamp-2",
              // Mobile: current styling
              "text-muted-foreground",
              // Desktop: white text for dark overlay
              "md:text-white/80"
            )}>
              {description}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
